import EyeTexturePath from "@lincolncenter/ui/eye-icon.png";
import FollowTheEyeTexturePath from "@lincolncenter/ui/follow_the_eye.png";
import LookThisWayTexturePath from '@lincolncenter/ui/look_this_way.png';
import LincolnCenterEventEmitter from "@/projects/lincolncenter/events";
import EyeVideoTexture from "@lincolncenter/ui/eye-animation.mp4";
import { IAssetBase, IAssetTexture, IAssetVideo } from "@/apps/app.content";
import { getCameraViewMatrix } from "../core/content.common";
import ContentLincolnCenterArrow from "./content.lincolncenter.arrow";
import { Calibration } from "./content.lincolncenter.types";
import {
  Angle,
  Camera,
  Engine,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  Scene,
  ShaderMaterial,
  StandardMaterial,
  TransformNode,
  Vector3,
} from "babylonjs";
import ContentLincolnCenterModelVideo from "./content.lincolncenter.modelvideo";
import { vibrate } from "@/core/vibrate/vibrate";
import { ContentLoadHandler } from "../core/content.loader";

export default class ContentLincolnCenterCalibration extends ContentLincolnCenterModelVideo {
  private isCalibrating: boolean = false;
  private root: TransformNode | undefined;
  private currentlyTracking: Mesh | undefined;
  private lastCameraRotation: Vector3 = new Vector3(0, 0, 0);
  private cameraPos: Vector3 | undefined;
  private cameraForward: Vector3 | undefined;
  private cameraRotation: Vector3 | undefined;
  private targetAlpha: number = 1;
  private rotationAngle: number = 0;
  private lastTime: number = 0;
  private deltaTime: number = 0;
  private targetScale: number = 1;
  private baseScale: Vector3 = new Vector3(1, 1, 1);
  private followEyeTexture: IAssetTexture | undefined;
  private lookThisWayTexture: IAssetTexture | undefined;

  private arrow: ContentLincolnCenterArrow | undefined;

  constructor() {
    super();
    this.id = Calibration;
    this.loopVideo = true;
    this.checkVideoSynchronization = false;
  }

  public init(_engine: Engine, _scene: Scene, _camera: Camera): void {
    super.init(_engine, _scene, _camera);
    this.loadContents();

    this.root = new TransformNode("calibrationRoot", this.scene);
    // create a plane to track
    const plane = MeshBuilder.CreatePlane(
      "trackingPlane",
      { 
        width: 1,
        height:1,
      },
      this.scene,
    );
    const material = new StandardMaterial("trackingPlaneMaterial", this.scene);
    
    plane.material = material;

    this.currentlyTracking = plane;
    this.currentlyTracking.parent = this.root;

    // set plane position to be in front of the camera
    this.currentlyTracking.position = new Vector3(0, 0, -3);
    this.updateTransforms();
    this.lastCameraRotation = this.cameraRotation ?? new Vector3(0, 0, 0);

    this.setEnabledForHierarchy(this.root, false);
    this.targetAlpha = 0.0;
  }


  private async loadContents() {
    this.lookThisWayTexture = await ContentLoadHandler.loadAsset<IAssetTexture>(
      {
        type: "texture",
        id: "look-this-way-texture",
        path: LookThisWayTexturePath,
      }
    )
  }

  private setEnabledForHierarchy(node: TransformNode, enabled: boolean) {
    node.getChildMeshes().forEach((mesh) => {
      mesh.isVisible = enabled;
    });
  }

  public setArrowUI (arrow: ContentLincolnCenterArrow) {
    this.arrow = arrow;
  }

  public get assets(): IAssetBase[] {
    return [
      {
        type: "texture",
        id: "eye-texture",
        path: EyeTexturePath,
      },
      {
        type: "texture",
        id: "follow-eye-texture",
        path: FollowTheEyeTexturePath
      },
      {
        type: "video",
        id: "eye-video",
        path: EyeVideoTexture
      }
    ];
  }

  public loadAssetComplete(_asset: IAssetBase): void {
     if (_asset.id === "follow-eye-texture") {
      const _texture = _asset as IAssetTexture;
      const texture = _texture.texture;
      this.followEyeTexture = _texture;

      if (texture) {
        texture.hasAlpha = true;
      }
    }
    else if (_asset.type === "video") {
      const _video = _asset as IAssetVideo;
      this.video = _video;
      
      this.applyAlphaVideoToMesh(this.currentlyTracking!, _video.videoTexture!, this.scene!);
      if (this.currentlyTracking?.material instanceof ShaderMaterial) {
        this.currentlyTracking.material.setFloat("alphaDiscardThreshold", 0.5);
      }
    }
  }

 

  private lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }

  private lerpVector3(a: Vector3, b: Vector3, t: number) {
    return new Vector3(
      this.lerp(a.x, b.x, t),
      this.lerp(a.y, b.y, t),
      this.lerp(a.z, b.z, t)
    );
  }

  private updateVisuals() {
    if (this.root) {
      this.root!.rotation = new Vector3(
        0,
        this.lastCameraRotation!.y -
        Angle.FromDegrees(this.rotationAngle).radians(),
        0
      );
    }

    if (this.arrow) {
      this.arrow.pointTo(this.currentlyTracking!.getAbsolutePosition());
    }
  }

  public setCameraMatrix(matrix: Matrix): void {
    if (matrix) {
      this.cameraForward = Vector3.TransformNormal(
        new Vector3(0, 0, -1),
        matrix
      ).normalize();

      const scale = new Vector3();
      const rotation = new Quaternion();
      const position = new Vector3();

      // Decompose the matrix
      matrix.decompose(scale, rotation, position);

      // Convert the quaternion to Euler angles
      this.cameraRotation = rotation.toEulerAngles();
      this.cameraPos = position;
    }
  }

  //todo: pulsate & vibrate every second of being locked in
  //todo: disable the arrow after it is done (force show = false;;;)

  public startCalibration(): void {
    if (this.isCalibrating) {
      return;
    }

    if (this.camera) {
      if (this.currentlyTracking?.material) {
        this.currentlyTracking!.material!.alpha = 0;
        if (this.currentlyTracking.material instanceof ShaderMaterial)
          (this.currentlyTracking.material as ShaderMaterial).setFloat("alpha", 0);
      }

      this.setEnabledForHierarchy(this.root!, true);
      this.updateTransforms();
      this.lastCameraRotation = this.cameraRotation ?? new Vector3(0, 0, 0);
      this.isCalibrating = true;
      this.rotationAngle = -30;
      this.targetAlpha = 0.5;
      this.targetScale = 1.0;
      this.playContent();
    }

    if (this.arrow && this.followEyeTexture && this.followEyeTexture.texture) {
      this.arrow.arrowShowOverride = true;
      this.arrow.setArrowTextTexture(this.followEyeTexture.texture);
    }
  }

  public stopCalibration(): void {
    this.isCalibrating = false;
    this.targetAlpha = 0.0;
    this.targetScale = 0.0;
    if (this.arrow) {
      this.arrow.hideArrow();      
      if (this.lookThisWayTexture && this.lookThisWayTexture.texture)
        this.arrow.setArrowTextTexture(this.lookThisWayTexture!.texture!);
    }
    setTimeout(() => {
      if (this.isCalibrating === false && this.root) {
        this.setEnabledForHierarchy(this.root!, false);
      }
    }, 2000);

    if (Engine.audioEngine) {
      Engine.audioEngine.audioContext?.suspend();
    }
  }

  private updateTransforms() {
    if (this.scene) {
      const activeCamera = this.scene.activeCamera!;

      const worldMatrix = getCameraViewMatrix(activeCamera).invert();
      const cameraForward = activeCamera.getForwardRay(
        100,
        worldMatrix
      ).direction;

      // Variables to hold scale, rotation, and position
      const scale = new Vector3();
      const rotation = new Quaternion();
      const position = new Vector3();

      // Decompose the matrix into scale, rotation, and position
      worldMatrix.decompose(scale, rotation, position);
      this.cameraPos = position;
      this.cameraForward = cameraForward;
      this.cameraRotation = rotation.toEulerAngles();
    }
  }

  private lockedInTimer: number = 0;

  public render(): void {

    if (
      this.video &&
      this.video.videoTexture &&
      this.video.videoTexture.video &&
      this.currentlyTracking
    ) {
      this.updateTransforms();

      this.deltaTime = (performance.now() - this.lastTime) / 1000;
      this.lastTime = performance.now();

      if (this.cameraPos && this.cameraForward && this.cameraRotation) {
        if (this.isCalibrating) {
          const directionToPlane = this.currentlyTracking!.getAbsolutePosition()
            .subtract(this.cameraPos)
            .normalize();

          const ang = Angle.BetweenTwoVectors(
            this.cameraForward,
            directionToPlane
          );
          const fov = this.scene?.activeCamera!.fov ?? 0.8;

          if (ang.radians() < fov + 0.1) {
            let speedPerc = ang.radians() / (fov + 0.1);

            speedPerc = Math.min(1, speedPerc);
            speedPerc = Math.max(0.3, speedPerc);

            // invert the speed
            speedPerc = 1 - speedPerc;

            this.rotationAngle += 15 * this.deltaTime * speedPerc;
            if (this.rotationAngle > 360) {
              LincolnCenterEventEmitter.emit("calibration-completed");
              this.rotationAngle = 0;
            }

            // check if
            if (ang.degrees() < 10) {
              this.targetAlpha = 1.0;
              this.targetScale = 1.3;
              this.lockedInTimer += this.deltaTime;
              if (this.lockedInTimer > 1) {
                // vibration
                vibrate([200, 200]);
                this.lockedInTimer = 0;
              }
            } else {
              this.targetAlpha = 0.5;
              this.targetScale = 1.0;
              this.lockedInTimer = 1;
            }
          }

          //
          this.root?.position.set(
            this.cameraPos!.x,
            this.cameraPos!.y,
            this.cameraPos!.z
          );

          this.updateVisuals();
        }

        // Do this outside of the calibration check
        if (this.currentlyTracking?.material) {
          // lerp towards target alpha with delta time
          this.currentlyTracking.material.alpha = this.lerp(
            this.currentlyTracking.material.alpha,
            this.targetAlpha,
            5 * this.deltaTime
          );
          if (this.currentlyTracking.material instanceof ShaderMaterial)
            (this.currentlyTracking.material as ShaderMaterial).setFloat(
              "alpha",
              this.lerp(
                this.currentlyTracking.material.alpha,
                this.targetAlpha,
                5 * this.deltaTime
              )
            );

          if (this.currentlyTracking) {
            const lerpedScale = this.lerpVector3(
              this.currentlyTracking.scaling,
              this.baseScale.scale(this.targetScale),
              5 * this.deltaTime
            );
            this.currentlyTracking!.scaling = lerpedScale;
          }
        }
      }
    }
  }
}
